// The pricing recommendation, ported from the proposal builder prototype
// (olytic-cpq recommend() and considered()): a value-based one-time price held
// within the policy's premium cap and discount floor, nudged down until the
// customer's 3-year return clears the target, with the reasons in words.
import { roiModel, type ValueModel, valueTotals } from './valueModel.ts';

/** What pricing reads from a quote as the builder loads it (QuoteBuilder in the UI). */
export interface QuotePricingData {
  quote: { kind: 'new' | 'change_order' };
  lines: { hours: number | null; included: boolean; billing: 'one_time' | 'monthly'; list: number }[];
  totals: { oneTime: { list: number; included: number }; monthly: { list: number; replaced: number } };
}

export interface PricingPolicy {
  value_share_pct: number;
  max_premium_pct: number;
  max_discount_pct: number;
  hourly_floor: number;
  target_payback_months: number;
  roi_target: number;
  default_pm_pct: number;
  philosophy: string | null;
}

export interface PricingInputs {
  /** One-time list price (included work excluded). */
  list: number;
  /** One-time list with included work at its list price. */
  fullList: number;
  /** Hours behind the hourly lines on the quote, and their list total. */
  hours: number;
  hourlyList: number;
  /** The monthly cost the customer's return is weighed against. */
  monthly: number;
}

export type PricingMode = 'list' | 'premium' | 'discount';

export interface Recommendation {
  oneTime: number;
  monthly: number;
  /** The value-based price: risk-weighted 3-year value x the policy's share. */
  fair: number;
  /** Our floor and our cap. */
  low: number;
  high: number;
  mode: PricingMode;
  notes: string[];
  payback: number | null;
}

/** What the recommendation weighs: one-time list, the hours behind estimated work, and the monthly cost at list. */
export function pricingInputs(data: QuotePricingData): PricingInputs {
  // One-time work with an estimate (its product's hours, or its Price Grid cell's).
  const hourly = data.lines.filter((l) => (l.hours ?? 0) > 0 && !l.included && l.billing === 'one_time');
  const { oneTime, monthly } = data.totals;
  return {
    list: oneTime.list,
    fullList: oneTime.list + oneTime.included,
    hours: hourly.reduce((total, l) => total + (l.hours ?? 0), 0),
    hourlyList: hourly.reduce((total, l) => total + l.list, 0),
    monthly: monthly.list - (data.quote.kind === 'change_order' ? monthly.replaced : 0),
  };
}

const r500 = (n: number) => Math.floor(n / 500 + 0.5) * 500;
const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const one1 = (n: number) => Math.round(n * 10) / 10;

/** Months to pay back `oneTime` from the monthly benefit at full run rate, net of the monthly cost; null if it never does. */
export function paybackAt(model: ValueModel, oneTime: number, monthly: number): number | null {
  const net = valueTotals(model).gross / 12 - monthly;
  return net <= 0 ? null : oneTime / net;
}

/** The customer's 3-year return (benefits over costs) at a one-time price. */
export const returnAt = (model: ValueModel, oneTime: number, monthly: number) => roiModel(model, oneTime, monthly, 36).multiple;

export function recommend(model: ValueModel, policy: PricingPolicy, inputs: PricingInputs): Recommendation {
  const totals = valueTotals(model);
  const { list: L, monthly } = inputs;
  const notes: string[] = [];
  const fair = r500(totals.adjusted * 3 * (policy.value_share_pct / 100));
  const high = r500(L * (1 + policy.max_premium_pct / 100));
  const lowPct = L * (1 - policy.max_discount_pct / 100);
  const paidShare = inputs.fullList ? L / inputs.fullList : 1;
  // The hourly floor: list less whatever the hourly work is priced above the floor rate. (The
  // prototype held list when a quote had no hourly work; here no hourly work means no hourly floor.)
  const lowRate = inputs.hours ? L - Math.max(0, (inputs.hourlyList - policy.hourly_floor * inputs.hours) * paidShare) : lowPct;
  const low = r500(Math.max(lowPct, lowRate));
  let oneTime = L;
  let mode: PricingMode = 'list';

  if (!totals.gross) {
    notes.push('No value model yet, so this holds list. Add metrics on the Value tab to price to value.');
  } else if (fair > L * 1.1) {
    oneTime = Math.min(fair, high);
    mode = oneTime > L ? 'premium' : 'list';
    notes.push(`The value-based price (${money(fair)}) is above list, so the services price can carry a premium of up to ${policy.max_premium_pct}%.`);
    if (fair > high) notes.push('Value supports even more than the cap. Rather than a bigger services premium, grow the Foundation commitment or add scope that raises the value further.');
  } else if (fair < L * 0.95) {
    oneTime = Math.max(fair, low);
    mode = 'discount';
    notes.push(`The value-based price (${money(fair)}) is below list, so this discounts services toward it.`);
    if (fair < low) notes.push(`Value doesn't clear our discount floor (${money(low)}). Hold the floor and consider cutting scope, or confirm a bigger benefit with the customer.`);
    notes.push('Foundation stays at list: discount services before recurring.');
  } else {
    notes.push(`List is within a fair range of the value-based price (${money(fair)}), so hold list.`);
  }
  oneTime = Math.abs(oneTime - L) < 1 ? L : r500(oneTime);

  const target = policy.roi_target;
  if (totals.gross && target > 0 && returnAt(model, oneTime, monthly) < target && oneTime > low) {
    const before = oneTime;
    const beforeReturn = returnAt(model, oneTime, monthly);
    const kept = notes.filter((n) => !n.includes('so hold list'));
    notes.length = 0;
    notes.push(...kept);
    let a = low;
    let b = oneTime;
    if (returnAt(model, a, monthly) >= target) {
      for (let i = 0; i < 30; i += 1) {
        const mid = (a + b) / 2;
        if (returnAt(model, mid, monthly) >= target) a = mid;
        else b = mid;
      }
      oneTime = Math.max(low, Math.floor(a / 500) * 500);
    } else {
      oneTime = low;
    }
    if (oneTime < L) mode = 'discount';
    notes.push(
      `At ${money(before)}${Math.abs(before - L) < 1 ? ' (list)' : ''} the customer's 3-year return is ${one1(beforeReturn)}x, short of our ${target}x target. ` +
        `The highest price that clears it, rounded down to $500, is ${money(oneTime)}${oneTime <= low ? ' (our floor)' : ''}.`,
    );
  }

  const payback = paybackAt(model, oneTime, monthly);
  const paybackTarget = policy.target_payback_months;
  if (payback !== null && payback > paybackTarget) {
    notes.push(
      mode === 'discount'
        ? `Even at this price, payback is ${Math.round(payback)} months. The case needs more value or less scope.`
        : `Payback runs ${Math.round(payback)} months, past the ${paybackTarget}-month target. Consider phasing the work or confirming more value.`,
    );
  }
  if (payback === null && totals.gross) notes.push("The monthly benefit doesn't cover the monthly license, so there is no payback at this price.");
  return { oneTime, monthly, fair, low, high, mode, notes, payback };
}

export interface PricePoint {
  label: string;
  oneTime: number;
  payback: number | null;
  /** 3-year return multiple. */
  return3: number;
  verdict: string;
  recommended: boolean;
}

/** "Why this price": list, the value-based price, our floor and our cap, each with what it means. Equal prices merge. */
export function pricesConsidered(model: ValueModel, policy: PricingPolicy, inputs: PricingInputs, rec: Recommendation): PricePoint[] {
  const { list: L, monthly } = inputs;
  const target = policy.roi_target;
  const point = (label: string, oneTime: number, verdict: string): PricePoint => ({
    label,
    oneTime,
    payback: paybackAt(model, oneTime, monthly),
    return3: returnAt(model, oneTime, monthly),
    verdict,
    recommended: Math.abs(oneTime - rec.oneTime) < 1,
  });
  const listReturn = returnAt(model, L, monthly);
  const points: PricePoint[] = [
    point(
      'List price',
      L,
      Math.abs(L - rec.oneTime) < 1
        ? "Holds our anchor and still clears the customer's payback and return targets."
        : L < rec.oneTime
          ? 'Leaves money on the table: the value supports more than list.'
          : `Harder to win: at list the customer's 3-year return is ${one1(listReturn)}x${target ? ` against a ${target}x target` : ''}.`,
    ),
  ];
  if (rec.fair > 0) {
    points.push(
      point(
        'Value-based price',
        rec.fair,
        rec.fair > rec.high
          ? 'Above our premium cap. Asking for it risks the deal; grow Foundation or scope instead.'
          : rec.fair < rec.low
            ? 'Below our floor. Taking it would undercut our rates.'
            : returnAt(model, rec.fair, monthly) < target
              ? "Where the value model points, but the customer's 3-year return falls short of the target."
              : 'Where the value model points: a fair share of what the customer gains.',
      ),
    );
  }
  points.push(point("Lowest we'd go", rec.low, 'Our floor. Only to save the deal, and only in exchange for something: speed, term, a reference or a logo.'));
  const highPayback = paybackAt(model, rec.high, monthly);
  const highReturn = returnAt(model, rec.high, monthly);
  points.push(
    point(
      "Highest we'd go",
      rec.high,
      highPayback !== null && highPayback <= policy.target_payback_months && highReturn >= target
        ? `Our premium cap. The customer would still pay back in about ${Math.round(highPayback)} months with a ${one1(highReturn)}x return.`
        : "Our premium cap. At this price the customer's case weakens.",
    ),
  );
  if (!points.some((p) => p.recommended)) points.push(point('Recommended', rec.oneTime, rec.notes.at(-1) ?? ''));
  const seen = new Map<number, PricePoint>();
  return points.filter((p) => {
    const key = Math.round(p.oneTime);
    const prior = seen.get(key);
    if (prior) {
      prior.label += ` · ${p.label}`;
      prior.recommended ||= p.recommended;
      return false;
    }
    seen.set(key, p);
    return true;
  });
}
